import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../services/api';
import { Link, useParams } from 'react-router-dom';
import DescriptionDisplay from '../components/DescriptionDisplay';
import PageActions from '../components/PageActions';
import { formatOvernightArrivalDescription } from '../utils/activityDescriptions';
import { toMinutes } from '../utils/travelLegs';
import { sortByDate } from '../utils/sort';
import { buildLodgingEventsByDateDest } from '../utils/lodgingEvents';
import {
  formatTime24,
  formatDateLocal,
  formatDateReadable,
  formatWeekday,
  getDatesInRange,
  getDatesForDestination,
} from '../utils/dateTime';
import { buildGoogleMapsUrl } from '../utils/formatters';
import '../App.css';

function summarizePlaceName(text) {
  const parts = String(text || '').split(',').map(p => p.trim()).filter(Boolean);
  if (!parts.length) return text || '—';
  const short = parts.slice(0, 2).join(', ');
  if (short.length <= 42) return short;
  return parts[0];
}

export default function ItineraryDayView({ embedded }) {
  const { tripId } = useParams();
  const [tripName, setTripName] = useState('');
  const [destinations, setDestinations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sunEvents, setSunEvents] = useState([]);
  const [lodgings, setLodgings] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);
  const [currentDate, setCurrentDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [
          trips,
          dests,
          acts,
          suns,
          lodgs,
          types,
          cats
        ] = await Promise.all([
          fetch(apiUrl('/api/trips')).then(res => res.ok ? res.json() : []),
          fetch(apiUrl(`/api/trips/${tripId}/destinations`)).then(res => res.ok ? res.json() : []),
          fetch(apiUrl(`/api/trips/${tripId}/activities`)).then(res => res.ok ? res.json() : []),
          fetch(apiUrl(`/api/trips/${tripId}/sun_events`)).then(res => res.ok ? res.json() : []),
          fetch(apiUrl(`/api/trips/${tripId}/lodgings`)).then(res => res.ok ? res.json() : []),
          fetch(apiUrl('/api/activity_types')).then(res => res.ok ? res.json() : []),
          fetch(apiUrl('/api/activity_categories')).then(res => res.ok ? res.json() : []),
        ]);

        if (cancelled) return;

        setDestinations(Array.isArray(dests) ? dests : []);
        setActivities(Array.isArray(acts) ? acts : []);
        setSunEvents(Array.isArray(suns) ? suns : []);
        setLodgings(Array.isArray(lodgs) ? lodgs : []);
        setActivityTypes(Array.isArray(types) ? types : []);
        setActivityCategories(Array.isArray(cats) ? cats : []);

        const trip = (Array.isArray(trips) ? trips : []).find(t => String(t.trip_id) === String(tripId));
        setTripName(trip?.trip_name || '');
      } catch {
        if (cancelled) return;
        setDestinations([]);
        setActivities([]);
        setSunEvents([]);
        setLodgings([]);
        setActivityTypes([]);
        setActivityCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [tripId]);

  const sortedDestinations = useMemo(() => sortByDate(destinations, 'start_date'), [destinations]);

  const typeById = useMemo(() => {
    const map = new Map();
    (Array.isArray(activityTypes) ? activityTypes : []).forEach(t => map.set(String(t.type_id), t));
    return map;
  }, [activityTypes]);

  const categoryById = useMemo(() => {
    const map = new Map();
    (Array.isArray(activityCategories) ? activityCategories : []).forEach(c => map.set(String(c.category_id), c));
    return map;
  }, [activityCategories]);

  const getTypeName = (typeId) => {
    const t = typeById.get(String(typeId));
    return t ? String(t.type_name || '') : '';
  };

  const getCategoryNameForType = (typeId) => {
    const t = typeById.get(String(typeId));
    if (!t) return '';
    const c = categoryById.get(String(t.category_id));
    return c ? String(c.category_name || '') : '';
  };

  const destinationById = useMemo(() => {
    const map = {};
    (Array.isArray(destinations) ? destinations : []).forEach(d => {
      if (d && d.destination_id) map[String(d.destination_id)] = d;
    });
    return map;
  }, [destinations]);

  const destinationTzById = useMemo(() => {
    const m = {};
    (Array.isArray(destinations) ? destinations : []).forEach(d => {
      if (d && d.destination_id) m[String(d.destination_id)] = d.timezone || 'UTC';
    });
    return m;
  }, [destinations]);

  const clampDateToDestination = useMemo(() => (dateStr, destId) => {
    const d = destinationById[String(destId)] || null;
    if (!d) return dateStr;
    const s = String(d.start_date || '').slice(0, 10);
    const e = String(d.end_date || '').slice(0, 10);
    if (!s || !e || !dateStr) return dateStr;
    if (dateStr < s) return s;
    if (dateStr > e) return e;
    return dateStr;
  }, [destinationById]);

  const dayDestinations = useMemo(() => {
    const map = {};
    sortedDestinations.forEach(dest => {
      getDatesForDestination(dest).forEach(date => {
        if (!map[date]) map[date] = [];
        map[date].push(dest);
      });
    });
    return map;
  }, [sortedDestinations]);

  const dayListFromDestinations = useMemo(() => {
    if (!sortedDestinations.length) return [];
    const first = sortedDestinations[0]?.start_date || '';
    const last = sortedDestinations.reduce((acc, d) => {
      const end = String(d.end_date || '');
      if (!acc) return end;
      return end > acc ? end : acc;
    }, '');
    if (!first || !last) return [];
    return getDatesInRange(first, last);
  }, [sortedDestinations]);

  const activityDateFallback = useMemo(() => {
    const set = new Set();
    (Array.isArray(activities) ? activities : []).forEach(act => {
      const destTz = destinationTzById[String(act?.destination_id || '')] || 'UTC';
      const dateStr = formatDateLocal(act?.start_time, destTz) || String(act?.start_time || '').slice(0, 10);
      if (dateStr) set.add(String(dateStr).slice(0, 10));
    });
    return Array.from(set).sort();
  }, [activities, destinationTzById]);

  const dayList = dayListFromDestinations.length ? dayListFromDestinations : activityDateFallback;

  useEffect(() => {
    if (dayList.length && !dayList.includes(currentDate)) {
      setCurrentDate(dayList[0]);
    } else if (!dayList.length && currentDate) {
      setCurrentDate('');
    }
  }, [dayList, currentDate]);

  const sunByDateDest = useMemo(() => {
    const m = {};
    (Array.isArray(sunEvents) ? sunEvents : []).forEach(item => {
      const key = `${item.date}_${item.destination_id}`;
      const list = Array.isArray(item.events) ? item.events : [];
      m[key] = list;
    });
    return m;
  }, [sunEvents]);

  const lodgingEventsByDateDest = useMemo(() => buildLodgingEventsByDateDest(lodgings), [lodgings]);

  const activityEntriesByDate = useMemo(() => {
    const out = {};
    const seenByDate = {};

    const typeNameById = (typeId) => {
      const t = (Array.isArray(activityTypes) ? activityTypes : []).find(x => String(x.type_id) === String(typeId));
      return t ? String(t.type_name || '') : '';
    };

    const markPrimaryForDate = (dateStr, activityId) => {
      const k = String(dateStr || '');
      const id = String(activityId || '');
      if (!k || !id) return false;
      if (!seenByDate[k]) seenByDate[k] = new Set();
      if (seenByDate[k].has(id)) return false;
      seenByDate[k].add(id);
      return true;
    };

    const dayOffset = (fromDateStr, toDateStr) => {
      const a = String(fromDateStr || '').slice(0, 10);
      const b = String(toDateStr || '').slice(0, 10);
      if (!a || !b) return 0;
      try {
        const da = new Date(`${a}T00:00:00Z`);
        const db = new Date(`${b}T00:00:00Z`);
        if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return 0;
        return Math.round((db.getTime() - da.getTime()) / 86400000);
      } catch {
        return 0;
      }
    };

    (Array.isArray(activities) ? activities : []).forEach((act) => {
      const destId = act?.destination_id;
      const destKey = destId ? String(destId) : '';
      const destTz = destinationTzById[destKey] || 'UTC';

      const legs = Array.isArray(act?.legs) ? act.legs : [];
      if (legs.length) {
        const sortedLegs = [...legs].sort((a, b) => (Number(a?.leg_index) || 0) - (Number(b?.leg_index) || 0));

        sortedLegs.forEach((leg, idx) => {
          const depTz = leg?.from_tz || destTz;
          const rawDate = String(
            leg?.depart_local_date ||
            formatDateLocal(leg?.depart_time || act?.start_time, depTz) ||
            (leg?.depart_time ? String(leg.depart_time).slice(0, 10) : '') ||
            ''
          ).slice(0, 10);
          const rowDate = clampDateToDestination(rawDate, destId);
          if (!rowDate) return;
          const key = rowDate;
          if (!out[key]) out[key] = [];

          const arrTz = leg?.to_tz || destTz;
          const depDate = String(
            leg?.depart_local_date ||
            formatDateLocal(leg?.depart_time, depTz) ||
            (leg?.depart_time ? String(leg.depart_time).slice(0, 10) : '') ||
            ''
          ).slice(0, 10);
          const arrDate = String(
            leg?.arrive_local_date ||
            formatDateLocal(leg?.arrive_time, arrTz) ||
            (leg?.arrive_time ? String(leg.arrive_time).slice(0, 10) : '') ||
            ''
          ).slice(0, 10);
          const arrDateClamped = clampDateToDestination(arrDate, destId);

          out[key].push({
            row_kind: 'leg',
            row_id: `leg-${act.activity_id}-${idx}-${rowDate}`,
            row_date: rowDate,
            end_date: arrDateClamped,
            activity: act,
            destination_id: destId,
            primary_for_day: markPrimaryForDate(key, act.activity_id),
            leg_index: Number(leg?.leg_index) || idx,
            leg,
            start_local: formatTime24(leg?.depart_time || act?.start_time, depTz),
            end_local: formatTime24(leg?.arrive_time || act?.end_time, arrTz),
            end_day_offset: dayOffset(depDate, arrDate),
          });

          // Overnight leg: also surface the landing on the arrival day.
          if (arrDateClamped && depDate && arrDate !== depDate) {
            if (!out[arrDateClamped]) out[arrDateClamped] = [];
            out[arrDateClamped].push({
              row_kind: 'arrival',
              row_id: `arrival-${act.activity_id}-${idx}-${arrDateClamped}`,
              row_date: arrDateClamped,
              end_date: arrDateClamped,
              activity: act,
              destination_id: destId,
              leg_index: Number(leg?.leg_index) || idx,
              leg,
              primary_for_day: false,
              start_local: formatTime24(leg?.arrive_time, arrTz),
              end_local: '',
            });
          }

          const nextLeg = sortedLegs[idx + 1];
          if (nextLeg && leg?.arrive_time && nextLeg?.depart_time) {
            const arriveTz = leg?.to_tz || destTz;
            const departTz = nextLeg?.from_tz || destTz;
            const arriveDate = String(
              leg?.arrive_local_date ||
              formatDateLocal(leg?.arrive_time, arriveTz) ||
              rowDate
            ).slice(0, 10);
            const departDate = String(
              nextLeg?.depart_local_date ||
              formatDateLocal(nextLeg?.depart_time, departTz) ||
              arriveDate
            ).slice(0, 10);
            const transferKey = arriveDate;
            if (!out[transferKey]) out[transferKey] = [];
            const aType = String(leg?.type_name || typeNameById(leg?.type_id) || '').trim().toLowerCase();
            const bType = String(nextLeg?.type_name || typeNameById(nextLeg?.type_id) || '').trim().toLowerCase();
            const title = (aType === 'flight' && bType === 'flight') ? 'Layover' : 'Connection';

            out[transferKey].push({
              row_kind: 'transfer',
              row_id: `transfer-${act.activity_id}-${idx}-${arriveDate}`,
              row_date: arriveDate,
              end_date: departDate,
              activity: act,
              destination_id: destId,
              primary_for_day: false,
              title,
              start_local: formatTime24(leg?.arrive_time, arriveTz),
              end_local: formatTime24(nextLeg?.depart_time, departTz),
            });
          }
        });

        return;
      }

      if (!act.start_time) return;
      const dateStr = formatDateLocal(act.start_time, destTz);
      if (!dateStr) return;
      const endDateStr = act.end_time ? formatDateLocal(act.end_time, destTz) : '';
      const offset = endDateStr ? dayOffset(dateStr, endDateStr) : 0;
      const key = dateStr;
      if (!out[key]) out[key] = [];
      out[key].push({
        row_kind: 'activity',
        row_id: `act-${act.activity_id}-${dateStr}`,
        row_date: dateStr,
        end_date: endDateStr,
        activity: act,
        destination_id: destId,
        primary_for_day: markPrimaryForDate(key, act.activity_id),
        start_local: formatTime24(act.start_time, destTz),
        end_local: formatTime24(act.end_time, destTz),
        end_day_offset: offset,
      });

      // Overnight activity: also show arrival on the end date
      if (offset > 0 && endDateStr) {
        const arrDateClamped = clampDateToDestination(endDateStr, destId);
        if (!arrDateClamped) return;
        if (!out[arrDateClamped]) out[arrDateClamped] = [];
        out[arrDateClamped].push({
          row_kind: 'arrival',
          row_id: `arrival-${act.activity_id}-${arrDateClamped}`,
          row_date: arrDateClamped,
          end_date: endDateStr,
          activity: act,
          destination_id: destId,
          primary_for_day: false,
          start_local: formatTime24(act.end_time, destTz),
          end_local: '',
        });
      }
    });

    return out;
  }, [activities, destinationTzById, activityTypes, clampDateToDestination]);

  const combinedEntriesByDate = useMemo(() => {
    const map = {};
    const push = (date, entry) => {
      const k = String(date || '').slice(0, 10);
      if (!k) return;
      if (!map[k]) map[k] = [];
      map[k].push(entry);
    };

    Object.entries(sunByDateDest).forEach(([key, events]) => {
      const [date, destId] = key.split('_');
      (Array.isArray(events) ? events : []).forEach((evt, idx) => {
        push(date, {
          row_kind: 'sun',
          row_id: `sun-${key}-${idx}`,
          title: evt?.label || 'Sun event',
          start_local: evt?.time || '',
          destination_id: destId,
        });
      });
    });

    Object.entries(lodgingEventsByDateDest).forEach(([key, events]) => {
      const [date, destId] = key.split('_');
      (Array.isArray(events) ? events : []).forEach((evt) => {
        push(date, { ...evt, destination_id: evt.destination_id || destId });
      });
    });

    Object.entries(activityEntriesByDate).forEach(([date, list]) => {
      (Array.isArray(list) ? list : []).forEach((entry) => push(date, entry));
    });

    Object.keys(map).forEach((date) => {
      map[date].sort((a, b) => {
        const am = toMinutes(a.start_local);
        const bm = toMinutes(b.start_local);
        if (am == null && bm == null) return 0;
        if (am == null) return 1;
        if (bm == null) return -1;
        return am - bm;
      });
    });

    return map;
  }, [sunByDateDest, lodgingEventsByDateDest, activityEntriesByDate]);

  const entriesForCurrent = combinedEntriesByDate[currentDate] || [];
  const currentDestinations = dayDestinations[currentDate] || [];
  const currentIndex = dayList.findIndex(d => d === currentDate);

  const renderPlace = (full, fallback) => {
    const label = summarizePlaceName(full) || fallback;
    if (!full) return label;
    const href = buildGoogleMapsUrl(full);
    if (!href) return label;
    return (
      <a href={href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {label}
      </a>
    );
  };

  const renderBody = (entry) => {
    if (entry.row_kind === 'sun') return entry.title || 'Sun event';
    if (entry.row_kind === 'lodging') return null;
    if (entry.row_kind === 'arrival') {
      const desc = formatOvernightArrivalDescription(entry.activity || {});
      return desc ? <div style={{ color: 'var(--tp-muted)' }}>{desc}</div> : null;
    }
    if (entry.row_kind === 'transfer') {
      return (
        <div style={{ color: 'var(--tp-muted)' }}>
          {entry.title || 'Connection'}
        </div>
      );
    }

    const activity = entry.activity || {};
    const meeting = String(activity.meeting_address || '').trim();
    const dropoff = String(activity.dropoff_address || '').trim();
    const desc = String(activity.description || '').trim();
    const notes = String(activity.notes || '').trim();

    if (desc || notes) {
      return (
        <div style={{ display: 'grid', gap: 4 }}>
          {desc && <DescriptionDisplay text={activity.description} />}
          {notes && <DescriptionDisplay text={notes} />}
        </div>
      );
    }
    return meeting || dropoff ? (
      <div style={{ display: 'grid', gap: 4 }}>
        {meeting && <div style={{ color: 'var(--tp-muted)' }}>Meet: {renderPlace(meeting, meeting)}</div>}
        {dropoff && <div style={{ color: 'var(--tp-muted)' }}>Drop-off: {renderPlace(dropoff, dropoff)}</div>}
        {notes && <DescriptionDisplay text={notes} />}
      </div>
    ) : '—';
  };

  const timeLabel = (entry) => {
    const baseStart = entry.start_local || '—';
    if (entry.row_kind === 'sun') return baseStart;
    if (entry.row_kind === 'lodging') return baseStart || '—';
    const end = entry.end_local || '';
    const offset = Number(entry.end_day_offset) || 0;
    if (end && offset > 0) {
      return (
        <span style={{ whiteSpace: 'nowrap' }}>
          {baseStart} → {end}
          <sup style={{ fontSize: '0.7em', opacity: 0.85, marginLeft: 1 }}>+{offset}</sup>
        </span>
      );
    }
    if (end) return `${baseStart} → ${end}`;
    return baseStart;
  };

  const rowLabel = (entry) => {
    if (entry.row_kind === 'sun') return 'Sun';
    if (entry.row_kind === 'lodging') return entry.title || 'Lodging';
    if (entry.row_kind === 'transfer') return entry.title || 'Transfer';
    if (entry.row_kind === 'arrival') return 'Arrival';
    if (entry.row_kind === 'leg') return 'Travel';
    return 'Activity';
  };

  const titleContent = (entry) => {
    if (entry.row_kind === 'sun' || entry.row_kind === 'lodging' || entry.row_kind === 'transfer') {
      return entry.title || rowLabel(entry);
    }
    if (entry.row_kind === 'arrival') {
      const activity = entry.activity || {};
      return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <Link
            to={`/itinerary/${tripId}/activity/${activity.activity_id}`}
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {activity.title || 'Activity'}
          </Link>
          <span style={{ color: 'var(--tp-muted)', fontSize: 12 }}>— arrives</span>
        </div>
      );
    }
    const activity = entry.activity || {};
    const leg = entry.row_kind === 'leg' ? entry.leg : null;
    const legLabel = Number.isFinite(Number(entry.leg_index)) ? `Leg ${Number(entry.leg_index) + 1}` : 'Leg';
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        <Link
          to={`/itinerary/${tripId}/activity/${activity.activity_id}`}
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          {activity.title || 'Activity'}
        </Link>
        {leg && (
          <div style={{ color: 'var(--tp-muted)', fontSize: 12 }}>{legLabel}</div>
        )}
      </div>
    );
  };

  const destinationChip = (entry) => {
    const destId = entry.destination_id || entry?.activity?.destination_id;
    const dest = destinationById[String(destId)] || null;
    const name = dest?.destination_name || '';
    if (!name) return null;
    return <span className="tp-chip">{name}</span>;
  };

  const typeChip = (entry) => {
    if (entry.row_kind === 'sun') return null;
    const typeId = entry?.leg?.type_id || entry?.activity?.type_id;
    const typeName = getTypeName(typeId);
    if (!typeName) return null;
    return <span className="tp-chip">{typeName}</span>;
  };

  const categoryChip = (entry) => {
    if (entry.row_kind === 'sun' || entry.row_kind === 'lodging') return null;
    const typeId = entry?.leg?.type_id || entry?.activity?.type_id;
    const categoryName = getCategoryNameForType(typeId);
    if (!categoryName) return null;
    return <span className="tp-chip tp-chip--muted">{categoryName}</span>;
  };

  return (
    <div className="App">
      {!embedded && (
        <section className="tp-hero">
          <p className="tp-hero__kicker">Itinerary</p>
          <h1 className="tp-hero__title">{tripName ? `${tripName} — Day view` : 'Day-by-day itinerary'}</h1>
          <p className="tp-hero__subtitle">
            Browse one day at a time with quick navigation, destination context, and travel details.
          </p>
          <div className="print-hide">
            <PageActions
              actions={[
                { label: 'Full itinerary', to: `/itinerary/${tripId}?view=overview`, variant: 'solid' },
                { label: 'Manage trips', to: '/trips', variant: 'ghost' },
                { label: 'Home', to: '/', variant: 'ghost' },
              ]}
            />
          </div>
        </section>
      )}

      <section className="tp-section">
        <div className="tp-card">
          <div className="tp-day-nav">
            <button
              type="button"
              onClick={() => {
                if (currentIndex > 0) setCurrentDate(dayList[currentIndex - 1]);
              }}
              disabled={!dayList.length || currentIndex <= 0}
            >
              ‹ Prev day
            </button>
            <select
              className="tp-day-nav__select"
              value={currentDate || ''}
              onChange={(e) => setCurrentDate(e.target.value)}
              disabled={!dayList.length}
            >
              {dayList.length === 0 ? (
                <option value="">No dated days yet</option>
              ) : (
                dayList.map(date => (
                  <option key={date} value={date}>
                    {formatDateReadable(date)} ({formatWeekday(date)})
                  </option>
                ))
              )}
            </select>
            <button
              type="button"
              onClick={() => {
                if (currentIndex >= 0 && currentIndex < dayList.length - 1) {
                  setCurrentDate(dayList[currentIndex + 1]);
                }
              }}
              disabled={!dayList.length || currentIndex === dayList.length - 1 || currentIndex === -1}
            >
              Next day ›
            </button>
          </div>

          <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{currentDate ? formatDateReadable(currentDate) : 'No day selected'}</div>
              <div style={{ color: 'var(--tp-muted)', marginTop: 4 }}>{currentDate ? formatWeekday(currentDate) : ''}</div>
            </div>
            <div className="tp-row" style={{ justifyContent: 'flex-end' }}>
              {currentDestinations.map(dest => (
                <span key={dest.destination_id} className="tp-chip">
                  {dest.destination_name}
                </span>
              ))}
              <span className="tp-pill">Events: {entriesForCurrent.length}</span>
            </div>
          </div>
        </div>

        <div className="tp-card" style={{ marginTop: 12 }}>
          {loading ? (
            <p style={{ color: 'var(--tp-muted)', margin: 0 }}>Loading day details...</p>
          ) : dayList.length === 0 ? (
            <p style={{ color: 'var(--tp-muted)', margin: 0 }}>Add destinations or activities with dates to see the day-by-day view.</p>
          ) : entriesForCurrent.length === 0 ? (
            <p style={{ color: 'var(--tp-muted)', margin: 0 }}>No events scheduled for this day.</p>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {entriesForCurrent.map((entry) => (
                <div key={entry.row_id} className="tp-card tp-card--tight tp-day-entry">
                  <div className="tp-day-entry__header">
                    <span className="tp-pill">{rowLabel(entry)}</span>
                    <span className="tp-day-entry__time">{timeLabel(entry)}</span>
                  </div>

                  <div className="tp-day-entry__title">
                    {titleContent(entry)}
                  </div>

                  <div className="tp-day-entry__meta">
                    {destinationChip(entry)}
                    {typeChip(entry)}
                    {categoryChip(entry)}
                  </div>

                  <div className="tp-day-entry__body">
                    {renderBody(entry)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
